/**
 * UAPC Anytime - Vendor MVS Sync Page
 * @module VendorSync
 * @author Adil Khan - Backend Developer
 * @description Motor Vehicle Software sync controls
 * @version 1.0.0
 * Git: Adil Khan <adil@linesoft.com> - MVS sync implementation
 */

import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, AlertCircle, Clock, Play } from 'lucide-react';
import { Button } from '../../components/ui/button';
import VendorLayout from '../../components/VendorLayout';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VendorSync = () => {
  const { user } = useAuth();
  const [syncHistory, setSyncHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchSyncHistory();
  }, []);

  const fetchSyncHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mvs/sync/history`);
      setSyncHistory(response.data);
    } catch (error) {
      console.error('Failed to load sync history:', error);
    } finally {
      setLoading(false);
    }
  };

  const triggerSync = async (syncType) => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API_URL}/api/mvs/sync`, {
        vendor_id: user.id,
        sync_type: syncType
      });
      
      if (response.data.status === 'completed') {
        toast.success(`Sync completed! ${response.data.items_synced} items synced.`);
      } else {
        toast.info('Sync initiated');
      }
      
      fetchSyncHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'in_progress':
        return <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-slate-400" />;
    }
  };

  return (
    <VendorLayout>
      <div className="p-8" data-testid="vendor-sync">
        <h1 className="font-heading font-black text-3xl text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Motor Vehicle Software Sync
        </h1>

        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-6 mb-8">
          <h3 className="font-heading font-bold text-lg text-blue-900 dark:text-blue-100 uppercase mb-2">
            About MVS Sync
          </h3>
          <p className="text-blue-800 dark:text-blue-200 text-sm mb-4">
            Synchronize your UAPC inventory with Motor Vehicle Software. This ensures your parts 
            are listed across both platforms with consistent pricing and availability.
          </p>
          <p className="text-blue-700 dark:text-blue-300 text-xs">
            Note: This is a simulated sync for demonstration purposes.
          </p>
        </div>

        {/* Sync Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white uppercase">
                  Full Sync
                </h3>
                <p className="text-sm text-slate-500">Sync all inventory items</p>
              </div>
            </div>
            <Button
              onClick={() => triggerSync('full')}
              disabled={syncing}
              className="w-full btn-industrial"
              data-testid="full-sync-btn"
            >
              {syncing ? (
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Syncing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Play className="w-4 h-4" />
                  Start Full Sync
                </span>
              )}
            </Button>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white uppercase">
                  Incremental Sync
                </h3>
                <p className="text-sm text-slate-500">Sync only changed items</p>
              </div>
            </div>
            <Button
              onClick={() => triggerSync('incremental')}
              disabled={syncing}
              variant="outline"
              className="w-full h-12 font-heading font-bold uppercase tracking-wider"
              data-testid="incremental-sync-btn"
            >
              {syncing ? (
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Syncing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Play className="w-4 h-4" />
                  Start Incremental Sync
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Sync History */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6">
          <h3 className="font-heading font-bold text-xl text-slate-900 dark:text-white uppercase mb-6">
            Sync History
          </h3>

          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 bg-slate-200 dark:bg-slate-800 animate-pulse" />
              ))}
            </div>
          ) : syncHistory.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No sync history yet</p>
          ) : (
            <div className="space-y-4">
              {syncHistory.map((sync, index) => (
                <div 
                  key={sync.id} 
                  className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700"
                  data-testid={`sync-history-${index}`}
                >
                  <div className="flex items-center gap-4">
                    {getStatusIcon(sync.status)}
                    <div>
                      <div className="font-medium text-slate-900 dark:text-white capitalize">
                        {sync.sync_type} Sync
                      </div>
                      <div className="text-sm text-slate-500">
                        {new Date(sync.started_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`px-2 py-1 text-xs font-bold uppercase ${
                      sync.status === 'completed' ? 'bg-green-100 text-green-800' :
                      sync.status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {sync.status}
                    </div>
                    {sync.items_synced > 0 && (
                      <div className="text-xs text-slate-500 mt-1">
                        {sync.items_synced} items synced
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </VendorLayout>
  );
};

export default VendorSync;
